import React, { useEffect, useRef } from 'react';
import apiClient from '../api/client';
import { usePageCacheState } from '../contexts/PageCacheContext';
import { useAuth } from '../contexts/AuthContext';
import ErrorMessage from '../components/ErrorMessage';
import { Email, Keyword, EmailAccount } from '../components/mail/types';
import MailHeader from '../components/mail/MailHeader';
import MailControls from '../components/mail/MailControls';
import KeywordForm from '../components/mail/KeywordForm';
import EmailAccountSelector from '../components/mail/EmailAccountSelector';
import MailList from '../components/mail/MailList';
import MailDetail from '../components/mail/MailDetail';
import SubscriptionGuard from '../components/common/SubscriptionGuard';

interface KeywordFormFields {
  name: string;
  regex_pattern: string;
  description: string;
  is_case_sensitive: boolean;
}

const MailTrackingContent: React.FC = () => {
  const { refreshAuth } = useAuth();
  const [selectedEmail, setSelectedEmail] = usePageCacheState<Email | null>(
    'mailTracking.selectedEmail',
    null
  );

  const [searchTerm, setSearchTerm] = usePageCacheState<string>(
    'mailTracking.searchTerm',
    ''
  );
  const [timeFilter, setTimeFilter] = usePageCacheState<string>(
    'mailTracking.timeFilter',
    'all'
  );
  const [selectedKeyword, setSelectedKeyword] = usePageCacheState<string>(
    'mailTracking.selectedKeyword',
    ''
  );
  const [emails, setEmails] = usePageCacheState<Email[]>(
    'mailTracking.emails',
    []
  );
  const [emailAccounts, setEmailAccounts] = usePageCacheState<EmailAccount[]>(
    'mailTracking.emailAccounts',
    []
  );
  const [selectedEmailAccount, setSelectedEmailAccount] =
    usePageCacheState<string>('mailTracking.selectedEmailAccount', '');
  const [keywords, setKeywords] = usePageCacheState<Keyword[]>(
    'mailTracking.keywords',
    []
  );
  const [showKeywordForm, setShowKeywordForm] = usePageCacheState<boolean>(
    'mailTracking.showKeywordForm',
    false
  );
  const [editingKeyword, setEditingKeyword] = usePageCacheState<any>(
    'mailTracking.editingKeyword',
    null
  );
  const [loading, setLoading] = usePageCacheState<boolean>(
    'mailTracking.loading',
    true
  );
  const [emailsLoading, setEmailsLoading] = usePageCacheState<boolean>(
    'mailTracking.emailsLoading',
    false
  );
  const [error, setError] = usePageCacheState<string | null>(
    'mailTracking.error',
    null
  );
  const [keywordForm, setKeywordForm] = usePageCacheState(
    'mailTracking.keywordForm',
    {
      name: '',
      regex_pattern: '',
      description: '',
      is_case_sensitive: false,
    } as KeywordFormFields
  );
  const initialFetchRef = useRef(false);

  const loadEmailAccounts = React.useCallback(async (retryCount = 0) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await apiClient.get('/email-accounts', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 10000
      });
      const accounts = response.data.data || [];
      setEmailAccounts(accounts.map((acc: any): EmailAccount => ({
        id: acc.id.toString(),
        email_address: acc.email_address,
        provider: acc.provider,
        imap_host: acc.imap_host,
        imap_port: acc.imap_port,
        is_active: acc.is_active
      })));

      if (accounts.length > 0) {
        const stillExists = accounts.some(
          (account: any) => account.id.toString() === selectedEmailAccount
        );
        if (!selectedEmailAccount || !stillExists) {
          setSelectedEmailAccount(accounts[0].id.toString());
        }
      } else {
        setSelectedEmailAccount('');
      }
    } catch (error: any) {
      if (error.response?.status === 401 && retryCount === 0) {
        const authRefreshed = await refreshAuth();
        if (authRefreshed) {
          setTimeout(() => loadEmailAccounts(1), 1000);
          return;
        } else {
          window.location.href = '/login';
          return;
        }
      }
      if (retryCount < 2 && (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED')) {
        setTimeout(() => loadEmailAccounts(retryCount + 1), 1000);
      }
    }
  }, [refreshAuth, selectedEmailAccount, setEmailAccounts, setSelectedEmailAccount]);

  const loadKeywords = React.useCallback(async (retryCount = 0) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await apiClient.get('/keywords', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 10000
      });
      if (response.data.success) {
        setKeywords(response.data.data.map((kw: any): Keyword => ({
          id: kw.id.toString(),
          name: kw.name,
          pattern: kw.regex_pattern,
          description: kw.description,
          is_case_sensitive: kw.is_case_sensitive
        })));
      }
    } catch (error: any) {
      if (error.response?.status === 401 && retryCount === 0) {
        const authRefreshed = await refreshAuth();
        if (authRefreshed) {
          setTimeout(() => loadKeywords(1), 1000);
          return;
        } else {
          window.location.href = '/login';
          return;
        }
      }
      if (retryCount < 2 && (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED')) {
        setTimeout(() => loadKeywords(retryCount + 1), 1000);
      }
    } finally {
      if (retryCount === 0) {
        setLoading(false);
      }
    }
  }, [refreshAuth, setKeywords, setLoading]);

  const loadEmailsFromAccount = React.useCallback(async (accountId: string, retryCount = 0) => {
    try {
      setEmailsLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await apiClient.get(`/email-accounts/${accountId}/emails`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 15000
      });

      if (response.data.success) {
        const apiEmails = response.data.data.map((email: any) => ({
          id: parseInt(email.id),
          subject: email.subject,
          sender: email.sender,
          category: email.matched_keywords && email.matched_keywords.length > 0 ? 'Kategorize' : 'Genel',
          categoryColor: email.matched_keywords && email.matched_keywords.length > 0 ? 'bg-green-500' : 'bg-gray-500',
          content: email.content,
          receivedAt: email.received_at,
          isRead: email.is_read,
          isStarred: false,
          isMatched: email.matched_keywords && email.matched_keywords.length > 0,
          matchedKeywords: email.matched_keywords || []
        }));
        setEmails(apiEmails);
      }
    } catch (error: any) {
      if (retryCount < 3 && (
        error.response?.status >= 500 ||
        error.code === 'NETWORK_ERROR' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'TIMEOUT'
      )) {
        const delay = Math.min(3000 * Math.pow(2, retryCount), 10000);
        setTimeout(() => loadEmailsFromAccount(accountId, retryCount + 1), delay);
        return;
      }
      if (error.response?.status === 401 && retryCount === 0) {
        const authRefreshed = await refreshAuth();
        if (authRefreshed) {
          setTimeout(() => loadEmailsFromAccount(accountId, 1), 1000);
          return;
        }
      }
      setError('Email\'ler yüklenirken hata oluştu: ' + (error.response?.data?.error?.message || error.message));
    } finally {
      if (retryCount === 0) {
        setEmailsLoading(false);
      }
    }
  }, [refreshAuth, setError, setEmails, setEmailsLoading]);

  // Load data on component mount
  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;
    loadEmailAccounts();
    loadKeywords();
  }, [loadEmailAccounts, loadKeywords]);

  // Load emails when email account is selected
  useEffect(() => {
    if (selectedEmailAccount) {
      loadEmailsFromAccount(selectedEmailAccount);
    } else {
      setEmails([]);
    }
  }, [selectedEmailAccount, loadEmailsFromAccount, setEmails]);

  const handleCreateKeyword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keywordForm.name.trim() || !keywordForm.regex_pattern.trim()) {
      alert('Keyword adı ve aranacak kelimeler zorunludur');
      return;
    }
    const words = keywordForm.regex_pattern.split(',').map((word: string) => word.trim()).filter((word: string) => word);
    const regexPattern = `(${words.join('|')})`;
    const keywordData = {
      name: keywordForm.name,
      regex_pattern: regexPattern,
      description: keywordForm.description,
      is_case_sensitive: keywordForm.is_case_sensitive
    };

    try {
      const token = localStorage.getItem('authToken');
      const response = await apiClient.post('/keywords', keywordData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        alert('Keyword başarıyla oluşturuldu!');
        setShowKeywordForm(false);
        setKeywordForm({ name: '', regex_pattern: '', description: '', is_case_sensitive: false });
        loadKeywords();
      }
    } catch (error: any) {
      alert('Keyword oluşturulamadı: ' + (error.response?.data?.error?.message || 'Bilinmeyen hata'));
    }
  };

  const handleDeleteKeyword = async (keywordId: string) => {
    if (!window.confirm('Bu keyword\'ü silmek istediğinizden emin misiniz?')) return;
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Giriş yapmanız gerekiyor');
      return;
    }
    try {
      const response = await apiClient.delete(`/keywords/${keywordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        loadKeywords();
        alert('Keyword başarıyla silindi!');
      }
    } catch (error: any) {
      alert('Keyword silinemedi: ' + (error.response?.data?.error?.message || 'Bilinmeyen hata'));
    }
  };

  const handleKeywordSelect = (keywordName: string) => {
    setSelectedKeyword(selectedKeyword === keywordName ? '' : keywordName);
  };

  const getKeywordMatchCount = (keywordName: string) => {
    return emails.filter((email: Email) =>
      email.matchedKeywords && email.matchedKeywords.includes(keywordName)
    ).length;
  };

  const handleEditKeyword = (keyword: Keyword) => {
    const regexPattern = keyword.pattern || '';
    const words = regexPattern.replace(/^\(|\)$/g, '').split('|');
    setEditingKeyword(keyword);
    setKeywordForm({
      name: keyword.name,
      regex_pattern: words.join(', '),
      description: keyword.description || '',
      is_case_sensitive: keyword.is_case_sensitive
    });
    setShowKeywordForm(true);
  };

  const handleUpdateKeyword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keywordForm.name.trim() || !keywordForm.regex_pattern.trim()) {
      alert('Keyword adı ve aranacak kelimeler zorunludur');
      return;
    }
    const words = keywordForm.regex_pattern.split(',').map((word: string) => word.trim()).filter((word: string) => word);
    const regexPattern = `(${words.join('|')})`;
    const keywordData = {
      name: keywordForm.name,
      regex_pattern: regexPattern,
      description: keywordForm.description,
      is_case_sensitive: keywordForm.is_case_sensitive
    };

    try {
      const token = localStorage.getItem('authToken');
      const response = await apiClient.put(`/keywords/${editingKeyword.id}`, keywordData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        alert('Keyword başarıyla güncellendi!');
        setShowKeywordForm(false);
        setEditingKeyword(null);
        setKeywordForm({ name: '', regex_pattern: '', description: '', is_case_sensitive: false });
        loadKeywords();
      }
    } catch (error: any) {
      alert('Keyword güncellenemedi: ' + (error.response?.data?.error?.message || 'Bilinmeyen hata'));
    }
  };

  const timeFilters = [
    { id: 'all', name: 'Tümü', count: emails.length },
    {
      id: 'today', name: 'Bugün', count: emails.filter(e => {
        const emailDate = new Date(e.receivedAt);
        const today = new Date();
        return emailDate.toDateString() === today.toDateString();
      }).length
    },
    {
      id: 'week', name: 'Bu Hafta', count: emails.filter(e => {
        const emailDate = new Date(e.receivedAt);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return emailDate >= weekAgo;
      }).length
    },
    {
      id: 'month', name: 'Bu Ay', count: emails.filter(e => {
        const emailDate = new Date(e.receivedAt);
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return emailDate >= monthAgo;
      }).length
    }
  ];

  const filteredEmails = emails.filter((email: Email) => {
    const term = searchTerm || '';
    const matchesSearch = (email.subject?.toLowerCase() || '').includes(term.toLowerCase()) ||
      (email.sender?.toLowerCase() || '').includes(term.toLowerCase()) ||
      (email.content?.toLowerCase() || '').includes(term.toLowerCase());

    const matchesTime = timeFilter === 'all' || (() => {
      const emailDate = new Date(email.receivedAt);
      const now = new Date();
      switch (timeFilter) {
        case 'today': return emailDate.toDateString() === now.toDateString();
        case 'week':
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return emailDate >= weekAgo;
        case 'month':
          const monthAgo = new Date();
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return emailDate >= monthAgo;
        default: return true;
      }
    })();

    const matchesKeyword = selectedKeyword === '' || (email.matchedKeywords && email.matchedKeywords.includes(selectedKeyword));
    return matchesSearch && matchesTime && matchesKeyword;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffInHours < 1) return 'Az önce';
    if (diffInHours < 24) return `${diffInHours} saat önce`;
    if (diffInHours < 48) return 'Dün';
    return date.toLocaleDateString('tr-TR');
  };

  const highlightKeywords = (text: string, keywords: string[]) => {
    let highlightedText = text;
    keywords.forEach(keyword => {
      const regex = new RegExp(`(${keyword})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
    });
    return highlightedText;
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {error && (
        <div className="p-4">
          <ErrorMessage
            message={error}
            onDismiss={() => setError(null)}
            onRetry={() => {
              loadEmailAccounts();
              loadKeywords();
            }}
          />
        </div>
      )}

      <MailHeader
        unreadCount={emails.filter(e => !e.isRead).length}
        selectedKeyword={selectedKeyword}
        filteredCount={filteredEmails.length}
        onRefresh={() => {
          loadEmailAccounts();
          loadKeywords();
          if (selectedEmailAccount) {
            loadEmailsFromAccount(selectedEmailAccount);
          }
        }}
        onAddKeyword={() => setShowKeywordForm(true)}
      />

      <div className="flex-1 overflow-y-auto flex flex-col">
        <MailControls
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          timeFilter={timeFilter}
          onTimeFilterChange={setTimeFilter}
          timeFilters={timeFilters}
          keywords={keywords}
          selectedKeyword={selectedKeyword}
          onKeywordSelect={handleKeywordSelect}
          onEditKeyword={handleEditKeyword}
          onDeleteKeyword={handleDeleteKeyword}
          onToggleKeywordForm={() => setShowKeywordForm(!showKeywordForm)}
          showKeywordForm={showKeywordForm}
          getKeywordMatchCount={getKeywordMatchCount}
        />

        {showKeywordForm && (
          <KeywordForm
            editingKeyword={editingKeyword}
            form={keywordForm}
            onFormChange={setKeywordForm}
            onSubmit={editingKeyword ? handleUpdateKeyword : handleCreateKeyword}
            onCancel={() => {
              setShowKeywordForm(false);
              setEditingKeyword(null);
              setKeywordForm({
                name: '',
                regex_pattern: '',
                description: '',
                is_case_sensitive: false
              });
            }}
          />
        )}

        <EmailAccountSelector
          emailAccounts={emailAccounts}
          selectedEmailAccount={selectedEmailAccount}
          onSelect={setSelectedEmailAccount}
        />

        <div className="flex-1 flex overflow-hidden">
          <MailList
            loading={loading}
            emailsLoading={emailsLoading}
            selectedEmailAccount={selectedEmailAccount}
            filteredEmails={filteredEmails}
            selectedEmail={selectedEmail}
            onSelectEmail={setSelectedEmail}
            searchTerm={searchTerm}
            formatDate={formatDate}
          />

          <MailDetail
            email={selectedEmail}
            formatDate={formatDate}
            highlightKeywords={highlightKeywords}
          />
        </div>
      </div>
    </div>
  );
};

const MailTracking: React.FC = () => (
  <SubscriptionGuard requiredPlan="mail">
    <MailTrackingContent />
  </SubscriptionGuard>
);

export default MailTracking;
